import { type IColumn, type IObjectWithKey, Selection } from "@fluentui/react";
import * as React from "react";
import { type UUID, createUuid } from "../../utils/uuid";
import { observer } from "mobx-react";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import {
    type MotionSensorsApiResource,
    type MotionSensorsQuery,
} from "../../domain/repositories/repository-motion-sensors";
import { LayoutList } from "../layouts/layout-list";
import { omit } from "ramda";
import { action, computed, makeObservable } from "mobx";
import type { MotionSensorSortKey, MotionSensor } from "../../api";
import type { PaginationState } from "../../utils/pagination-state";
import type { SelectionState } from "../../utils/selection-state";
import { defaultPageSize } from "../../utils/constants";
import type { SortState } from "../../utils/sort-state";
import { ListMotionSensorItemCell } from "../atoms/list-motion-sensor-item-cell";
import { store } from "../../store";

export interface ListMotionSensorsProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface ListMotionSensorItem extends IObjectWithKey {
    readonly key: UUID;
}

@observer
export class ListMotionSensors extends React.Component<ListMotionSensorsProps> {
    private _listStateId = createUuid();

    private selection = new Selection({
        onSelectionChanged: () => this.updateSelection(),
    });

    constructor(props: ListMotionSensorsProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.motionSensors.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<MotionSensorSortKey> {
        return store.serviceListStates.motionSensors.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<MotionSensorsApiResource> {
        return store.serviceListStates.motionSensors.getPaginationState(this.listStateId);
    }

    @computed private get selectionState(): SelectionState {
        return store.serviceListStates.motionSensors.getSelectionState(this.listStateId);
    }

    @computed private get query(): MotionSensorsQuery {
        return { pageSize: defaultPageSize, ...this.sortState.query };
    }

    @computed private get motionSensors(): (MotionSensor | undefined)[] {
        return store.repositoryMotionSensors.byQuery(this.query, this.paginationState.pagination);
    }

    @computed private get items(): (ListMotionSensorItem | undefined)[] {
        return this.motionSensors.map((motionSensor) => {
            if (!motionSensor) {
                return;
            }

            return {
                key: motionSensor.id,
            };
        });
    }

    private renderItem(motionSensorId: UUID, column: IColumn): JSX.Element {
        return <ListMotionSensorItemCell motionSensorId={motionSensorId} column={column} />;
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [
            {
                name: store.i18n.t("listMotionSensors.column.vehicleDeviceSerialNumber.name"),
                key: "vehicleDeviceSerialNumber",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                name: store.i18n.t("listMotionSensors.column.firmwareLastUpdatedAt.name"),
                key: "firmwareLastUpdatedAt",
                minWidth: 100,
                maxWidth: 200,
            },
            {
                name: store.i18n.t("listMotionSensors.column.currentFirmwareVersion.name"),
                key: "currentFirmwareVersion",
                minWidth: 100,
                maxWidth: 200,
            },
        ];

        return columns;
    }

    @action.bound private updateSelection(): void {
        // Update the listState's selectionState to the new selected keys.
        this.selectionState.updateSelection(
            this.selection.getSelection().map((item) => item.key as UUID),
        );
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.motionSensors.isInitialized(this.listStateId)) {
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList paginationState={this.paginationState} commandBar={this.props.commandBar}>
                <ElofleetList
                    {...props}
                    // Disable right-click to stay consistent with other lists
                    onItemContextMenu={() => {}}
                    selection={this.selection}
                    onRenderItemColumn={(item: ListMotionSensorItem, _index, column) =>
                        this.renderItem(item.key, column!)
                    }
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns(this.columns)}
                    items={this.items}
                />
            </LayoutList>
        );
    }
}
